import { useMemo } from 'react';
import { useLocation } from 'react-router';
import { isNil, path, propOr } from '../utils/fp';
import { i18n, locales, getDatasource, defaultViewer, sortOptionIndex } from '../lib/settings';
import { fromSearchToState } from '../utils/router';
import { OVERVIEW, TABLE } from '../utils/constants';
import { cleanifyLayoutIds } from '../utils';

// The location react-router holds is the only source these hooks read, so what
// <Routes> matches and what they answer come from the same context and cannot
// disagree for a render.
//
// The empty defaults are module constants and the two hooks that build a value
// memoize it per call site. Both matter on the table render path: propOr({}, ...)
// and cleanifyLayoutIds() hand back a new object on every call, which is the
// "new object waterfall" the selectors carried memoize-one for -- and that cache
// held a single slot shared by every component, so two interleaved consumers
// thrashed it into never hitting.
const EMPTY_OBJECT = {};

export const useLocationState = () => {
  const { state, search } = useLocation();
  // An url that carries no history state -- shared, shortened or typed -- leaves
  // the query string as the only source, and parsing it builds a new object.
  return useMemo(() => state ?? fromSearchToState(search), [state, search]);
};

// <Router basename> strips the basename before it reaches the context, so this
// is already the application-relative path.
export const usePathname = () => useLocation().pathname;

export const useSortIndexSelected = () =>
  propOr(sortOptionIndex, 'sortIndexSelected', useLocationState());

export const useTerm = () => propOr('', 'term', useLocationState());

export const useSearchResultNb = () => propOr(0, 'searchResultNb', useLocationState());

export const useIsBypass = () => useLocationState()?.bypass;

export const useStart = () => useLocationState()?.start;

export const useLocale = () => propOr(i18n.localeId, 'locale', useLocationState());

export const useFacet = () => useLocationState()?.facet;

export const useConstraints = () => propOr(EMPTY_OBJECT, 'constraints', useLocationState());

export const useDataflow = () => propOr(EMPTY_OBJECT, 'dataflow', useLocationState());

export const useDataquery = () => propOr('', 'dataquery', useLocationState());

export const useHasAccessibility = () => useLocationState()?.hasAccessibility;

export const useHasDataAvailability = () => propOr(true, 'hasDataAvailability', useLocationState());

export const useIsDataAvaibilityInState = () => !isNil(useLocationState()?.hasDataAvailability);

export const useViewer = () => propOr(defaultViewer, 'viewer', useLocationState());

export const useMap = () => useLocationState()?.map;

export const usePeriod = () => useLocationState()?.period;

export const useDisplay = () => propOr('label', 'display', useLocationState());

export const useTimeDimensionOrders = () => propOr(EMPTY_OBJECT, 'time', useLocationState());

export const useIsOverview = () => OVERVIEW === useViewer();

export const useIsTable = () => TABLE === useViewer();

export const useHasLastNObservations = () => {
  const datasourceId = useDataflow()?.datasourceId;
  return propOr(false, 'hasLastNObservations', getDatasource(datasourceId));
};

export const useIsShareable = () => {
  const datasourceId = useDataflow()?.datasourceId;
  return propOr(true, 'isShareable', getDatasource(datasourceId));
};

export const useTableLayout = () => {
  const layout = useLocationState()?.layout;
  return useMemo(() => cleanifyLayoutIds(layout), [layout]);
};

export const useLastNObservations = () => useLocationState()?.lastNObservations;

export const useLastNMode = () => useLocationState()?.lastNMode;

export const useIsRtl = () => path([useLocale(), 'isRtl'], locales);
